/**
 * Aggregate sessionStorage 缓存（ADR-0003）。
 *
 * 同一 session 重复访问先用本地缓存渲染（首屏 0 等待），并发起轻量
 * `/api/version` 校验；版本不一致才重拉。跨 session 不复用。
 *
 * - **key 含 SCHEMA_VERSION**：payload 结构升级时 +1，旧格式读取直接
 *   判定 schema mismatch 并清缓存，避免读到不兼容结构假装成功。
 * - payload 内嵌 `cacheVersion` 双重校验（防 key 命名碰撞 + 内容篡改）。
 * - **Debug-First**：JSON 损坏 / schema 不匹配 → 清缓存 + console.error
 *   含失败原因 + 返 null；**不返旧数据、不静默兜底**。无缓存（key 不
 *   存在）返 null 是正常态，不 console.error。
 *
 * 函数内每次取 `globalThis.sessionStorage`，便于测试 stub / 隔离。
 */
import type { AggregateView } from '@/api/types';

/** payload 结构版本；字段语义变更时 +1，旧缓存自动失效。 */
const SCHEMA_VERSION = 4;
const STORAGE_KEY = `edu:showcase:aggregate:v${SCHEMA_VERSION}`;

export interface AggregateCachePayload {
  readonly cacheVersion: number;
  readonly contentVersion: string;
  readonly aggregate: AggregateView;
}

/** 暴露 key 供测试断言 schema version 防呆，不供外部直接读写。 */
export const AGGREGATE_CACHE_STORAGE_KEY = STORAGE_KEY;

/** 读缓存。无缓存返 null（正常态）；损坏/不兼容返 null + 清缓存 + console.error。 */
export function readAggregateCache(): AggregateCachePayload | null {
  const raw = storage().getItem(STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  return parsePayload(raw);
}

/** 写缓存。序列化/写入失败 → console.error，不抛（写缓存失败不阻断 ready）。 */
export function writeAggregateCache(aggregate: AggregateView, contentVersion: string): void {
  const payload: AggregateCachePayload = {
    cacheVersion: SCHEMA_VERSION,
    contentVersion,
    aggregate,
  };
  try {
    storage().setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[showcase-cache] write failed', error);
  }
}

/** 清缓存（损坏时调用 + 测试隔离用）。包 try/catch，removeItem 失败不抛。 */
export function clearAggregateCache(): void {
  try {
    storage().removeItem(STORAGE_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[showcase-cache] clear failed', error);
  }
}

/** 解析 + schema 校验。损坏/不兼容 → 清缓存 + console.error + 返 null。 */
function parsePayload(raw: string): AggregateCachePayload | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    clearAndLog('[showcase-cache] parse failed', error);
    return null;
  }
  if (!isCompatiblePayload(parsed)) {
    clearAndLog('[showcase-cache] schema mismatch', undefined);
    return null;
  }
  return parsed as AggregateCachePayload;
}

/** schema 校验：cacheVersion 一致 + contentVersion 字符串 + aggregate 是对象。 */
function isCompatiblePayload(value: unknown): value is AggregateCachePayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    v.cacheVersion === SCHEMA_VERSION &&
    typeof v.contentVersion === 'string' &&
    typeof v.aggregate === 'object' &&
    v.aggregate !== null
  );
}

function clearAndLog(message: string, error: unknown): void {
  clearAggregateCache();
  // eslint-disable-next-line no-console
  console.error(message, error ?? '');
}

function storage(): Storage {
  return globalThis.sessionStorage;
}
